import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/TranslationContext";
import { hasPermission } from "@/lib/permissions";
import { toast } from "sonner";
import { RefreshCw, Mail, Check, BellRing, Activity } from "lucide-react";

const BAND = {
  red: { chip: "bg-red-100 text-red-700", bar: "bg-red-500", ring: "border-red-200 bg-red-50" },
  amber: { chip: "bg-amber-100 text-amber-700", bar: "bg-amber-500", ring: "border-amber-200 bg-amber-50" },
  green: { chip: "bg-emerald-100 text-emerald-700", bar: "bg-emerald-500", ring: "border-emerald-200 bg-emerald-50" },
  na: { chip: "bg-gray-100 text-gray-500", bar: "bg-gray-300", ring: "border-black/5 bg-gray-50" },
};

function KriCard({ k }) {
  const { tx } = useI18n();
  const naCard = k.applicable === false;
  const b = BAND[naCard ? "na" : k.band] || BAND.green;
  return (
    <div className={`rounded-md border p-4 ${b.ring} ${naCard ? "opacity-70" : ""}`} data-testid={`kri-card-${k.kri_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-ek-ink/65">{tx(k.category)}</p>
          <h3 className="mt-0.5 font-display text-sm font-semibold text-ek-ink">{tx(k.name)}</h3>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${b.chip}`}
          title={naCard ? k.applicability_rationale : undefined}>
          {naCard ? "N/A" : k.band}
        </span>
      </div>
      {naCard ? (
        <p className="mt-2 text-xs italic text-ek-ink/65" data-testid={`kri-na-${k.kri_id}`}>
          Not material for your organisation — {tx(k.applicability_rationale)}
        </p>
      ) : (
        <p className="mt-2 font-display text-2xl font-bold text-ek-ink" data-testid={`kri-value-${k.kri_id}`}>{k.value_display}</p>
      )}
      <p className="mt-1 text-[11px] text-ek-ink/65">
        amber {k.amber_threshold} · red {k.red_threshold} · {k.direction === "higher_worse" ? "↑ worse" : "↓ worse"}
        {k.auto_tuned ? " · auto-tuned to materiality" : ""}
      </p>
    </div>
  );
}

export default function Alerts() {
  const { user } = useAuth();
  const canWrite = hasPermission(user, "risk.write");
  const [kris, setKris] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/kris").then(({ data }) => setKris(data)).catch(() => toast.error("Could not load KRIs"));
    api.get("/kris/alerts?state=active").then(({ data }) => setAlerts(data.items)).catch(() => {});
    window.dispatchEvent(new Event("kri:refresh"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const evaluate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/kris/evaluate");
      toast.success(`Evaluated ${data.evaluated} KRIs · ${data.active} active alerts`);
      load();
    } catch {
      toast.error("Evaluation failed");
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = async (id) => {
    try {
      await api.post(`/kris/alerts/${id}/acknowledge`);
      toast.success("Alert acknowledged");
      load();
    } catch {
      toast.error("Could not acknowledge");
    }
  };

  const notify = async (id) => {
    try {
      const { data } = await api.post(`/kris/alerts/${id}/notify`, {});
      if (data.sent) toast.success(`Emailed to ${data.recipient}`);
      else toast.error(data.error || "Email failed");
    } catch {
      toast.error("Email failed");
    }
  };

  const s = kris?.summary;

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Early-warning alerts" description="Key Risk Indicators and threshold breach alerts." path="/alerts" />
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-10" data-testid="alerts-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="ek-eyebrow">Early warning</h1>
            <h2 className="mt-1 font-display text-3xl font-bold text-ek-ink">KRIs &amp; alerts</h2>
            <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">
              Key Risk Indicators monitored against thresholds — breaches raise alerts you can acknowledge or escalate by email.
            </p>
          </div>
          {canWrite && (
            <button onClick={evaluate} disabled={busy} data-testid="kri-evaluate-button" className="ek-btn flex items-center gap-1.5 disabled:opacity-50">
              <RefreshCw size={15} className={busy ? "animate-spin" : ""} /> Evaluate now
            </button>
          )}
        </div>

        {s && (
          <div className="mt-6 grid grid-cols-3 gap-4" data-testid="kri-summary">
            <div className="rounded-md border border-red-200 bg-red-50 p-4"><p className="text-xs font-medium uppercase text-red-700/70">Red</p><p className="font-display text-2xl font-bold text-red-700">{s.red}</p></div>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4"><p className="text-xs font-medium uppercase text-amber-700/70">Amber</p><p className="font-display text-2xl font-bold text-amber-700">{s.amber}</p></div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4"><p className="text-xs font-medium uppercase text-emerald-700/70">Green</p><p className="font-display text-2xl font-bold text-emerald-700">{s.green}</p></div>
          </div>
        )}

        {/* Active alerts */}
        <h3 className="mt-10 ek-eyebrow flex items-center gap-1"><BellRing size={12} /> Active alerts</h3>
        <div className="mt-4 space-y-2" data-testid="active-alerts">
          {alerts.length === 0 ? (
            <p className="rounded-md border border-black/5 bg-white p-5 text-sm text-ek-ink/65">No active alerts — all KRIs within tolerance.</p>
          ) : (
            alerts.map((a) => {
              const b = BAND[a.band] || BAND.amber;
              return (
                <div key={a.alert_id} className="flex flex-wrap items-center gap-3 rounded-md border border-black/5 bg-white p-4" data-testid={`alert-${a.alert_id}`}>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${b.chip}`}>{a.band}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ek-ink">{a.message}</p>
                    <p className="text-xs text-ek-ink/65">{a.category} · owner {a.owner_role?.toUpperCase()}{a.emailed ? " · emailed" : ""}</p>
                  </div>
                  {canWrite && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => notify(a.alert_id)} data-testid={`alert-notify-${a.alert_id}`} className="ek-btn-outline !py-1.5 text-xs">
                        <Mail size={13} /> Email
                      </button>
                      <button onClick={() => acknowledge(a.alert_id)} data-testid={`alert-ack-${a.alert_id}`} className="ek-btn-outline !py-1.5 text-xs">
                        <Check size={13} /> Acknowledge
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* KRI board */}
        <h3 className="mt-10 ek-eyebrow flex items-center gap-1"><Activity size={12} /> KRI board</h3>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {kris?.kris.map((k) => <KriCard key={k.kri_id} k={k} />)}
        </div>
      </main>
    </div>
  );
}
